'use client';
import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { NinetailedProvider } from '@ninetailed/experience.js-react';
import dynamic from 'next/dynamic';
import NinetailedInsightsPlugin from '@ninetailed/experience.js-plugin-insights';
import NinetailedPreviewPlugin from '@ninetailed/experience.js-plugin-preview';
import { ExperienceConfiguration } from '@ninetailed/experience.js-next';

type Audience = {
    id: string;
    name: string;
    description?: string | undefined;
};

export default function Providers({
    children,
    preview = false,
    ninetailed,
}: {
    preview: boolean;
    children: React.ReactNode;
    ninetailed?: {
        preview: {
            allExperiences: ExperienceConfiguration[];
            allAudiences: Audience[];
        };
    };
}) {
    const showNtPreview = process.env.NEXT_PUBLIC_ENV !== 'production';
    return (
        <SmoothScroll>
            <PageScrollProvider>
                <StickyButtonProvider>
                    <NinetailedProvider
                        clientId={process.env['NEXT_PUBLIC_NINETAILED_API_KEY'] || ''}
                        environment={process.env['NEXT_PUBLIC_NINETAILED_ENVIRONMENT'] || ''}
                        plugins={[
                            new NinetailedInsightsPlugin(),
                            ...(showNtPreview
                                ? [
                                      new NinetailedPreviewPlugin({
                                          experiences: ninetailed?.preview.allExperiences || [],
                                          audiences: ninetailed?.preview.allAudiences || [],
                                          onOpenExperienceEditor: (experience) => {
                                              if (process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID) {
                                                  window.open(
                                                      `https://app.contentful.com/spaces/${
                                                          process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID
                                                      }/environments/${
                                                          process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || 'master'
                                                      }/entries/${experience.id}`,
                                                      '_blank'
                                                  );
                                              }
                                          },
                                          onOpenAudienceEditor: (audience) => {
                                              if (process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID) {
                                                  window.open(
                                                      `https://app.contentful.com/spaces/${
                                                          process.env.NEXT_PUBLIC_CONTENTFUL_SPACE_ID
                                                      }/environments/${
                                                          process.env.NEXT_PUBLIC_CONTENTFUL_ENVIRONMENT || 'master'
                                                      }/entries/${audience.id}`,
                                                      '_blank'
                                                  );
                                              }
                                          },
                                      }),
                                  ]
                                : []),
                        ]}
                    >
                        <ContentfulLivePreviewProvider
                            locale="en-US"
                            enableInspectorMode={preview}
                            enableLiveUpdates={preview}
                        >
                            {children}
                        </ContentfulLivePreviewProvider>
                    </NinetailedProvider>
                </StickyButtonProvider>
            </PageScrollProvider>
        </SmoothScroll>
    );
}

const SmoothScroll = dynamic(() => import('@fm-nx/ui').then((mod) => mod.SmoothScroll));
const PageScrollProvider = dynamic(() => import('@fm-nx/ui').then((mod) => mod.PageScrollProvider));
const StickyButtonProvider = dynamic(() => import('@fm-nx/ui').then((mod) => mod.StickyButtonProvider));
